/**
 * Canvas utility functions for common drawing operations
 */

import { ANIMATION_CONFIG } from '../constants';

/**
 * Smooths an array of points using weighted averaging
 */
export function smoothPoints(points: number[], passes: number = ANIMATION_CONFIG.GHOST_LINE_SMOOTHING_PASSES): number[] {
  const smoothed = [...points];
  
  for (let pass = 0; pass < passes; pass++) {
    for (let i = 1; i < smoothed.length - 1; i++) {
      smoothed[i] = (smoothed[i - 1] + smoothed[i] * 3 + smoothed[i + 1]) / 5;
    }
  }
  
  return smoothed;
}

/**
 * Interpolates a value at a specific position along an array
 */
export function interpolateAtPosition(points: number[], position: number): number {
  const index = position * (points.length - 1);
  const lowerIndex = Math.floor(index);
  const upperIndex = Math.ceil(index);
  
  if (lowerIndex === upperIndex) return points[lowerIndex];
  
  const t = index - lowerIndex;
  return points[lowerIndex] * (1 - t) + points[upperIndex] * t;
}

/**
 * Creates a gradient with multiple color stops
 */
export function createMultiStopGradient(
  ctx: CanvasRenderingContext2D,
  x0: number, y0: number,
  x1: number, y1: number,
  stops: Array<{ position: number; color: string }>
): CanvasGradient {
  const gradient = ctx.createLinearGradient(x0, y0, x1, y1);
  stops.forEach(stop => gradient.addColorStop(stop.position, stop.color));
  return gradient;
}

/**
 * Draws a path with glow effect
 */
export function drawGlowPath(
  ctx: CanvasRenderingContext2D,
  drawPath: () => void,
  color: string,
  glowIntensity: number,
  lineWidth: number = 2
): void {
  ctx.save();
  
  // Outer glow
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.shadowBlur = 20 * glowIntensity;
  ctx.shadowColor = color;
  
  drawPath();
  ctx.stroke();
  
  // Inner bright core
  ctx.strokeStyle = `rgba(255, 255, 255, ${0.2 * glowIntensity})`;
  ctx.lineWidth = 1;
  ctx.shadowBlur = 5;
  ctx.shadowColor = `rgba(255, 255, 255, ${0.5 * glowIntensity})`;
  ctx.stroke();
  
  ctx.restore();
}

/**
 * Converts an opacity value to a hex string for use in hex colors
 */
export function opacityToHex(opacity: number): string {
  return Math.round(opacity * 255).toString(16).padStart(2, '0');
}

/**
 * Creates a pulsing value between min and max using sine wave
 */
export function createPulse(time: number, speed: number, min: number, max: number): number {
  return min + Math.sin(time * speed) * max;
}

/**
 * Clamps a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/**
 * Maps a value from one range to another
 */
export function mapRange(
  value: number,
  inMin: number, inMax: number,
  outMin: number, outMax: number
): number {
  return ((value - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
}

/**
 * Easing function for smooth animations
 */
export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Easing function for smooth in animations
 */
export function easeInQuad(t: number): number {
  return t * t;
}